/**
 * Sticky Bar JavaScript
 * Gerencia funcionalidades do sticky bar incluindo dropdowns de filtros
 */

class StickyBar {
  constructor() {
    this.stickyBar = document.querySelector('[data-sticky-bar]');
    if (!this.stickyBar) return;

    this.filterButtons = this.stickyBar.querySelectorAll('.sticky-bar__filter-btn');
    this.currentOpenFilter = null;

    this.init();
  }

  init() {
    this.setupFilterButtons();
    this.setupClearButton();
    this.updateFilterButtons();
    this.updateClearButtonVisibility();
    this.setStickyBarHeight();
    this.addBodyClass();

    // Recalcular altura quando a janela redimensionar
    window.addEventListener('resize', () => {
      this.setStickyBarHeight();
    });

    // Garantir que o cart-drawer configure o ícone do sticky bar
    // Isso é necessário porque o sticky bar pode ser renderizado depois do cart-drawer
    this.ensureCartDrawerConfigured();
  }

  ensureCartDrawerConfigured() {
    // Aguardar um pouco para garantir que o cart-drawer foi inicializado
    setTimeout(() => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.setHeaderCartIconAccessibility === 'function') {
        cartDrawer.setHeaderCartIconAccessibility();
      }
    }, 100);
  }

  suportaHover() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  cancelarFechamento() {
    if (this.timerFechar) {
      clearTimeout(this.timerFechar);
      this.timerFechar = null;
    }
  }

  abrirPorHover(button) {
    this.cancelarFechamento();
    if (button.dataset.hasChildren !== 'true') return;
    if (this.currentOpenFilter === button) return;
    if (this.currentOpenFilter) this.closeFilter(this.currentOpenFilter);
    this.openFilter(button);
  }

  // Pequena folga para o mouse atravessar do botao ate o submenu sem fechar.
  agendarFechamento() {
    this.cancelarFechamento();
    this.timerFechar = setTimeout(() => {
      if (this.currentOpenFilter) this.closeFilter(this.currentOpenFilter);
    }, 180);
  }

  setupFilterButtons() {
    this.filterButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleFilter(button);
      });

      if (this.suportaHover()) {
        button.addEventListener('mouseenter', () => this.abrirPorHover(button));
        button.addEventListener('mouseleave', () => this.agendarFechamento());
      }
    });

    // Fechar dropdowns ao clicar fora
    this.clickOutsideHandler = (e) => {
      if (
        !e.target.closest('.sticky-bar__filter-btn') &&
        !e.target.closest('.sticky-bar__filter-dropdown')
      ) {
        this.closeAllFilters();
      }
    };
    document.addEventListener('click', this.clickOutsideHandler);

    // Fechar ao pressionar ESC
    this.escapeHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeAllFilters();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  toggleFilter(button) {
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const hasChildren = button.dataset.hasChildren === 'true';
    const linkUrl = button.dataset.linkUrl;

    // Se não tem filhos e tem URL, navegar diretamente
    if (!hasChildren && linkUrl && linkUrl !== '#' && linkUrl !== '') {
      window.location.href = linkUrl;
      return;
    }

    // Se o hover ja abriu, o clique mantem aberto em vez de fechar na cara do usuario.
    if (isExpanded && hasChildren && this.suportaHover()) {
      return;
    }

    if (isExpanded) {
      this.closeFilter(button);
    } else {
      // Fechar o filtro anteriormente aberto
      if (this.currentOpenFilter && this.currentOpenFilter !== button) {
        this.closeFilter(this.currentOpenFilter);
      }
      this.openFilter(button);
    }
  }

  openFilter(button) {
    button.setAttribute('aria-expanded', 'true');
    this.currentOpenFilter = button;

    // Criar ou mostrar dropdown
    this.createDropdown(button);
  }

  closeFilter(button) {
    button.setAttribute('aria-expanded', 'false');
    if (this.currentOpenFilter === button) {
      this.currentOpenFilter = null;
    }

    // Remover dropdown
    this.removeDropdown(button);
  }

  createDropdown(button) {
    // Remover dropdown existente se houver
    this.removeDropdown(button);

    const filterType = button.dataset.filter;
    const dropdown = document.createElement('div');
    dropdown.className = 'sticky-bar__filter-dropdown';
    dropdown.setAttribute('data-filter-type', filterType);

    // Conteúdo do dropdown baseado no tipo de filtro
    dropdown.innerHTML = this.getDropdownContent(filterType, button);

    // Posicionar dropdown abaixo do botão
    const buttonRect = button.getBoundingClientRect();
    dropdown.style.position = 'fixed';
    dropdown.style.top = `${buttonRect.bottom}px`;
    dropdown.style.left = `${buttonRect.left}px`;
    dropdown.style.minWidth = `${buttonRect.width}px`;
    
    // Ajustar posição horizontal em mobile se necessário
    if (window.innerWidth <= 749) {
      const rightEdge = buttonRect.right;
      if (rightEdge + 200 > window.innerWidth) {
        dropdown.style.left = `${window.innerWidth - 220}px`;
      }
    }

    document.body.appendChild(dropdown);

    if (this.suportaHover()) {
      dropdown.addEventListener('mouseenter', () => this.cancelarFechamento());
      dropdown.addEventListener('mouseleave', () => this.agendarFechamento());
    }

    // Adicionar event listeners aos itens do dropdown
    this.setupDropdownEvents(dropdown, filterType);
  }

  removeDropdown(button) {
    const filterType = button.dataset.filter;
    const existingDropdown = document.querySelector(
      `.sticky-bar__filter-dropdown[data-filter-type="${filterType}"]`
    );
    if (existingDropdown) {
      existingDropdown.remove();
    }
  }

  getDropdownContent(filterType, button) {
    // Verificar se é um item do menu com submenus
    const hasChildren = button && button.dataset.hasChildren === 'true';
    if (hasChildren) {
      const menuDataScript = document.getElementById('sticky-bar-menu-data');
      if (menuDataScript) {
        try {
          const menuData = JSON.parse(menuDataScript.textContent);
          const menuItem = menuData.find(item => item.handle.toLowerCase() === filterType);
          
          if (menuItem && menuItem.children && menuItem.children.length > 0) {
            let submenuHTML = '<div class="sticky-bar__dropdown-content">';
            menuItem.children.forEach((child) => {
              submenuHTML += `<a href="${child.url}" class="sticky-bar__dropdown-item">${child.title}</a>`;
            });
            submenuHTML += '</div>';
            return submenuHTML;
          }
        } catch (e) {
          console.error('Erro ao parsear dados do menu:', e);
        }
      }
    }
    
    // Buscar coleções do script tag JSON se for categorias
    if (filterType === 'categories') {
      const collectionsScript = document.getElementById('sticky-bar-collections');
      if (collectionsScript) {
        try {
          const collections = JSON.parse(collectionsScript.textContent);
          let categoriesHTML = '<div class="sticky-bar__dropdown-content">';
          collections.forEach((collection) => {
            categoriesHTML += `<a href="${collection.url}" class="sticky-bar__dropdown-item">${collection.title}</a>`;
          });
          categoriesHTML += '</div>';
          return categoriesHTML;
        } catch (e) {
          console.error('Erro ao parsear coleções:', e);
        }
      }
    }

    // Tentar buscar valores reais dos facets do Shopify
    if (filterType === 'sizes' || filterType === 'color') {
      const facetFormElement = document.querySelector('facet-filters-form');
      if (facetFormElement) {
        const paramName = this.findFilterParamName(filterType === 'sizes' ? 'size' : 'color');
        console.log('[StickyBar] Buscando dropdown para', filterType, 'com param:', paramName);
        
        if (paramName) {
          // Buscar no facet-filters-form inteiro, não só no form
          const checkboxes = facetFormElement.querySelectorAll(`input[name="${paramName}"]`);
          console.log('[StickyBar] Checkboxes encontrados para dropdown:', checkboxes.length);
          
          if (checkboxes.length > 0) {
            let optionsHTML = '<div class="sticky-bar__dropdown-content">';
            checkboxes.forEach((checkbox) => {
              // Se estiver desabilitado, pular
              if (checkbox.disabled) return;
              
              const label = checkbox.closest('label');
              const labelText = label ? label.textContent.trim() : checkbox.value;
              // Extrair apenas o nome da opção (remover contagem como "(5)")
              const cleanLabel = labelText.replace(/\s*\(\d+\)\s*$/, '').trim();
              const value = checkbox.value;
              
              console.log('[StickyBar] Adicionando opção:', cleanLabel, '=', value);
              
              if (filterType === 'sizes') {
                optionsHTML += `<button type="button" class="sticky-bar__dropdown-item" data-size="${this.escapeHtml(value)}">${this.escapeHtml(cleanLabel)}</button>`;
              } else {
                optionsHTML += `<button type="button" class="sticky-bar__dropdown-item" data-color="${this.escapeHtml(value)}">${this.escapeHtml(cleanLabel)}</button>`;
              }
            });
            optionsHTML += '</div>';
            return optionsHTML;
          }
        }
      }
    }

    // Fallback: valores padrão se não encontrar nos facets
    const contents = {
      categories: `
        <div class="sticky-bar__dropdown-content">
          <a href="/collections/all" class="sticky-bar__dropdown-item">Todas as Categorias</a>
        </div>
      `,
      sizes: `
        <div class="sticky-bar__dropdown-content">
          <button type="button" class="sticky-bar__dropdown-item" data-size="P">P</button>
          <button type="button" class="sticky-bar__dropdown-item" data-size="M">M</button>
          <button type="button" class="sticky-bar__dropdown-item" data-size="G">G</button>
          <button type="button" class="sticky-bar__dropdown-item" data-size="GG">GG</button>
        </div>
      `,
      promotion: `
        <div class="sticky-bar__dropdown-content">
          <a href="/collections/promocao" class="sticky-bar__dropdown-item">Em Promoção</a>
          <a href="/collections/novidades" class="sticky-bar__dropdown-item">Novidades</a>
          <a href="/collections/lancamentos" class="sticky-bar__dropdown-item">Lançamentos</a>
        </div>
      `,
      color: `
        <div class="sticky-bar__dropdown-content">
          <button type="button" class="sticky-bar__dropdown-item" data-color="Branco">Branco</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Preto">Preto</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Cinza">Cinza</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Azul">Azul</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Vermelho">Vermelho</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Verde">Verde</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Amarelo">Amarelo</button>
          <button type="button" class="sticky-bar__dropdown-item" data-color="Rosa">Rosa</button>
        </div>
      `,
      sort: `
        <div class="sticky-bar__dropdown-content">
          <button type="button" class="sticky-bar__dropdown-item" data-sort="relevance">Relevância</button>
          <button type="button" class="sticky-bar__dropdown-item" data-sort="price-ascending">Menor Preço</button>
          <button type="button" class="sticky-bar__dropdown-item" data-sort="price-descending">Maior Preço</button>
          <button type="button" class="sticky-bar__dropdown-item" data-sort="name-ascending">A-Z</button>
          <button type="button" class="sticky-bar__dropdown-item" data-sort="name-descending">Z-A</button>
        </div>
      `
    };

    return contents[filterType] || '<div class="sticky-bar__dropdown-content"><p>Sem opções disponíveis</p></div>';
  }

  setupDropdownEvents(dropdown, filterType) {
    const items = dropdown.querySelectorAll('.sticky-bar__dropdown-item');
    items.forEach((item) => {
      item.addEventListener('click', (e) => {
        if (filterType === 'sort') {
          e.preventDefault();
          const sortValue = e.target.dataset.sort || e.target.closest('[data-sort]')?.dataset.sort;
          if (sortValue) {
            this.handleSort(sortValue);
            this.closeAllFilters();
          }
        } else if (filterType === 'sizes') {
          e.preventDefault();
          const size = e.target.dataset.size || e.target.closest('[data-size]')?.dataset.size;
          if (size) {
            this.handleSizeFilter(size);
            this.closeAllFilters();
          }
        } else if (filterType === 'color') {
          e.preventDefault();
          const color = e.target.dataset.color || e.target.closest('[data-color]')?.dataset.color;
          if (color) {
            this.handleColorFilter(color);
            this.closeAllFilters();
          }
        } else {
          // Para links (categorias, promoção, submenus), verificar se é um link
          const linkUrl = item.getAttribute('href');
          if (linkUrl) {
            // É um link, deixar navegar normalmente
            // Fechar dropdown após um pequeno delay para permitir navegação
            setTimeout(() => {
              this.closeAllFilters();
            }, 100);
          } else {
            // Não é um link, fechar dropdown
            this.closeAllFilters();
          }
        }
      });
    });
  }

  handleSort(sortValue) {
    // Integrar com sistema de ordenação do Shopify
    const url = new URL(window.location.href);
    const filters = this.getActiveFilters();
    
    // Preservar filtros existentes
    if (filters.size) {
      url.searchParams.set('filter.v.option.tamanho', filters.size);
    }
    if (filters.color) {
      url.searchParams.set('filter.v.option.cor', filters.color);
    }
    
    // Adicionar ou atualizar ordenação
    url.searchParams.set('sort_by', sortValue);
    window.location.href = url.toString();
  }

  getActiveFilters() {
    const url = new URL(window.location.href);
    const filters = {};
    
    // Buscar todos os parâmetros de filtro da URL
    // O Shopify usa formatos como: filter.v.option.Tamanho, filter.v.option.Cor, etc
    // O nome da opção pode variar (Tamanho, Size, tamanho, etc)
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.v.option.')) {
        // Extrair o nome da opção (pode ser Tamanho, Cor, Size, Color, etc)
        const optionName = key.replace('filter.v.option.', '');
        // Normalizar para lowercase para comparação
        const normalizedName = optionName.toLowerCase();
        
        if (normalizedName.includes('tamanho') || normalizedName.includes('size')) {
          filters.size = { param: key, value: value, label: optionName };
        } else if (normalizedName.includes('cor') || normalizedName.includes('color')) {
          filters.color = { param: key, value: value, label: optionName };
        } else {
          // Armazenar outros filtros também
          if (!filters.others) filters.others = {};
          filters.others[key] = value;
        }
      }
    });
    
    filters.sort = url.searchParams.get('sort_by') || null;
    return filters;
  }

  hasActiveFilters() {
    const filters = this.getActiveFilters();
    return !!(filters.size || filters.color || (filters.others && Object.keys(filters.others).length > 0));
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  findFilterParamName(optionType) {
    // Tentar encontrar o nome do parâmetro correto nos facets
    const facetFormElement = document.querySelector('facet-filters-form');
    if (!facetFormElement) {
      console.log('[StickyBar] facet-filters-form não encontrado');
      return null;
    }
    
    // Buscar inputs de checkbox que correspondem ao tipo de filtro
    // Buscar no facet-filters-form inteiro, não só no form interno
    const checkboxes = facetFormElement.querySelectorAll('input[type="checkbox"][name^="filter.v.option."]');
    console.log('[StickyBar] Total de checkboxes encontrados:', checkboxes.length);
    
    const searchTerms = optionType === 'size' 
      ? ['tamanho', 'size'] 
      : ['cor', 'color'];
    
    for (const checkbox of checkboxes) {
      const paramName = checkbox.name;
      if (paramName && paramName.startsWith('filter.v.option.')) {
        const optionName = paramName.replace('filter.v.option.', '').toLowerCase();
        console.log('[StickyBar] Verificando param:', paramName, 'optionName:', optionName);
        
        if (searchTerms.some(term => optionName.includes(term))) {
          console.log('[StickyBar] Match encontrado para', optionType, ':', paramName);
          return paramName;
        }
      }
    }
    
    // Fallback: tentar nomes comuns
    console.log('[StickyBar] Usando fallback para', optionType);
    if (optionType === 'size') {
      return 'filter.v.option.Tamanho'; // Tamanho com T maiúsculo é comum no Shopify
    } else if (optionType === 'color') {
      return 'filter.v.option.Cor'; // Cor com C maiúsculo
    }
    
    return null;
  }

  handleSizeFilter(size) {
    console.log('[StickyBar] Aplicando filtro de tamanho:', size);
    
    // Tentar encontrar o checkbox/label nos facets e clicar diretamente
    const facetFormElement = document.querySelector('facet-filters-form');
    if (facetFormElement) {
      const paramName = this.findFilterParamName('size');
      console.log('[StickyBar] Nome do parâmetro encontrado:', paramName);
      
      if (paramName) {
        // Buscar todos os checkboxes com esse param_name
        const checkboxes = facetFormElement.querySelectorAll(`input[name="${paramName}"]`);
        console.log('[StickyBar] Checkboxes encontrados:', checkboxes.length);
        
        for (const checkbox of checkboxes) {
          console.log('[StickyBar] Comparando:', checkbox.value, 'com', size);
          
          // Comparar valores (case-insensitive e também tentar exato)
          if (checkbox.value.toLowerCase() === size.toLowerCase() || checkbox.value === size) {
            console.log('[StickyBar] Match encontrado! Clicando no checkbox/label');
            
            // Se o checkbox estiver desabilitado, não fazer nada
            if (checkbox.disabled) {
              console.log('[StickyBar] Checkbox desabilitado, ignorando');
              continue;
            }
            
            // Encontrar o label associado
            const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
            
            // Se tiver label, clicar no label (é mais natural)
            if (label) {
              label.click();
              console.log('[StickyBar] Clicou no label');
            } else {
              // Senão, clicar diretamente no checkbox
              checkbox.click();
              console.log('[StickyBar] Clicou no checkbox');
            }
            
            this.closeAllFilters();
            return;
          }
        }
      }
    }
    
    // Fallback: construir URL manualmente
    console.log('[StickyBar] Usando fallback - construindo URL manualmente');
    this.handleSizeFilterFallback(size);
  }
  
  handleSizeFilterFallback(size) {
    const url = new URL(window.location.href);
    const filters = this.getActiveFilters();
    
    // Remover filtros de tamanho existentes
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.v.option.') && (key.toLowerCase().includes('tamanho') || key.toLowerCase().includes('size'))) {
        url.searchParams.delete(key);
      }
    });
    
    // Preservar outros filtros
    if (filters.color && filters.color.param) {
      url.searchParams.set(filters.color.param, filters.color.value);
    }
    if (filters.others) {
      Object.entries(filters.others).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }
    if (filters.sort) {
      url.searchParams.set('sort_by', filters.sort);
    }
    
    // Adicionar novo filtro de tamanho
    const sizeParam = filters.size?.param || this.findFilterParamName('size') || 'filter.v.option.Tamanho';
    url.searchParams.set(sizeParam, size);
    window.location.href = url.toString();
  }

  handleColorFilter(color) {
    console.log('[StickyBar] Aplicando filtro de cor:', color);
    
    // Tentar encontrar o checkbox/label nos facets e clicar diretamente
    const facetFormElement = document.querySelector('facet-filters-form');
    if (facetFormElement) {
      const paramName = this.findFilterParamName('color');
      console.log('[StickyBar] Nome do parâmetro encontrado:', paramName);
      
      if (paramName) {
        // Buscar todos os checkboxes com esse param_name
        const checkboxes = facetFormElement.querySelectorAll(`input[name="${paramName}"]`);
        console.log('[StickyBar] Checkboxes encontrados:', checkboxes.length);
        
        for (const checkbox of checkboxes) {
          console.log('[StickyBar] Comparando:', checkbox.value, 'com', color);
          
          // Comparar valores (case-insensitive e também tentar exato)
          if (checkbox.value.toLowerCase() === color.toLowerCase() || checkbox.value === color) {
            console.log('[StickyBar] Match encontrado! Clicando no checkbox/label');
            
            // Se o checkbox estiver desabilitado, não fazer nada
            if (checkbox.disabled) {
              console.log('[StickyBar] Checkbox desabilitado, ignorando');
              continue;
            }
            
            // Encontrar o label associado
            const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
            
            // Se tiver label, clicar no label (é mais natural)
            if (label) {
              label.click();
              console.log('[StickyBar] Clicou no label');
            } else {
              // Senão, clicar diretamente no checkbox
              checkbox.click();
              console.log('[StickyBar] Clicou no checkbox');
            }
            
            this.closeAllFilters();
            return;
          }
        }
      }
    }
    
    // Fallback: construir URL manualmente
    console.log('[StickyBar] Usando fallback - construindo URL manualmente');
    this.handleColorFilterFallback(color);
  }
  
  handleColorFilterFallback(color) {
    const url = new URL(window.location.href);
    const filters = this.getActiveFilters();
    
    // Remover filtros de cor existentes
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.v.option.') && (key.toLowerCase().includes('cor') || key.toLowerCase().includes('color'))) {
        url.searchParams.delete(key);
      }
    });
    
    // Preservar outros filtros
    if (filters.size && filters.size.param) {
      url.searchParams.set(filters.size.param, filters.size.value);
    }
    if (filters.others) {
      Object.entries(filters.others).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }
    if (filters.sort) {
      url.searchParams.set('sort_by', filters.sort);
    }
    
    // Adicionar novo filtro de cor
    const colorParam = filters.color?.param || this.findFilterParamName('color') || 'filter.v.option.Cor';
    url.searchParams.set(colorParam, color);
    window.location.href = url.toString();
  }

  clearAllFilters() {
    const url = new URL(window.location.href);
    const filters = this.getActiveFilters();
    
    // Remover todos os filtros de opções de variantes
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.v.option.') || key.startsWith('filter.p.') || key.startsWith('filter.m.')) {
        url.searchParams.delete(key);
      }
    });
    
    // Manter ordenação se existir
    if (filters.sort) {
      url.searchParams.set('sort_by', filters.sort);
    }
    
    window.location.href = url.toString();
  }

  updateFilterButtons() {
    const filters = this.getActiveFilters();
    
    // Atualizar botão de tamanhos
    const sizeButton = this.stickyBar.querySelector('[data-filter="sizes"]');
    if (sizeButton && filters.size) {
      sizeButton.classList.add('sticky-bar__filter-btn--active');
      const filterText = sizeButton.querySelector('.sticky-bar__filter-text');
      if (filterText) {
        filterText.textContent = filters.size.value;
      }
    } else if (sizeButton) {
      sizeButton.classList.remove('sticky-bar__filter-btn--active');
      const filterText = sizeButton.querySelector('.sticky-bar__filter-text');
      if (filterText) {
        filterText.textContent = 'Tamanhos';
      }
    }
    
    // Atualizar botão de cor
    const colorButton = this.stickyBar.querySelector('[data-filter="color"]');
    if (colorButton && filters.color) {
      colorButton.classList.add('sticky-bar__filter-btn--active');
      const filterText = colorButton.querySelector('.sticky-bar__filter-text');
      if (filterText && filters.color.value) {
        // Capitalizar primeira letra
        const colorText = filters.color.value.charAt(0).toUpperCase() + filters.color.value.slice(1);
        filterText.textContent = colorText;
      }
    } else if (colorButton) {
      colorButton.classList.remove('sticky-bar__filter-btn--active');
      const filterText = colorButton.querySelector('.sticky-bar__filter-text');
      if (filterText) {
        filterText.textContent = 'Cor';
      }
    }
    
    // Atualizar botão de ordenar
    const sortButton = this.stickyBar.querySelector('[data-filter="sort"]');
    if (sortButton && filters.sort) {
      const sortLabels = {
        'relevance': 'Relevância',
        'price-ascending': 'Menor Preço',
        'price-descending': 'Maior Preço',
        'name-ascending': 'A-Z',
        'name-descending': 'Z-A'
      };
      const sortText = sortButton.querySelector('.sticky-bar__filter-text');
      if (sortText && sortLabels[filters.sort]) {
        sortText.textContent = sortLabels[filters.sort];
      }
    } else if (sortButton) {
      const sortText = sortButton.querySelector('.sticky-bar__filter-text');
      if (sortText) {
        sortText.textContent = 'Ordenar';
      }
    }
    
    // Atualizar visibilidade do botão limpar
    this.updateClearButtonVisibility();
  }

  setupClearButton() {
    const clearButton = this.stickyBar.querySelector('.sticky-bar__clear-filters');
    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }
  }

  updateClearButtonVisibility() {
    const clearButton = this.stickyBar.querySelector('.sticky-bar__clear-filters');
    if (clearButton) {
      if (this.hasActiveFilters()) {
        clearButton.classList.add('sticky-bar__clear-filters--visible');
      } else {
        clearButton.classList.remove('sticky-bar__clear-filters--visible');
      }
    }
  }

  closeAllFilters() {
    this.filterButtons.forEach((button) => {
      this.closeFilter(button);
    });
  }

  setStickyBarHeight() {
    if (!this.stickyBar) return;

    const height = this.stickyBar.offsetHeight;
    document.documentElement.style.setProperty('--sticky-bar-height', `${height}px`);

    // Para navegadores sem suporte a :has()
    const body = document.body;
    if (body) {
      body.classList.add('sticky-bar-active');
    }

    // O padding do MainContent é aplicado via CSS usando a variável --sticky-bar-height
    // Não precisamos aplicar inline style aqui
  }

  destroy() {
    // Cleanup event listeners
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    this.closeAllFilters();
  }

  addBodyClass() {
    document.body.classList.add('has-sticky-bar');
  }
}

// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyBar();
  });
} else {
  new StickyBar();
}

